import { Dispatch } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import i18n from 'i18next';
import {
  createGetHeaderData,
  createHeaderData,
  createPutHeaderData,
  createQueryParams,
  handlePreconditionFailed
} from 'utils/restUtils';
import { apiClient } from 'services/restService';
import { openSnackbar } from 'features/app/appSlice';
import { HttpHeaders } from 'models';
import { AppThunk } from 'models/store';
import {
  ChangeDeliveryPointAssignmentInterface,
  CreateDeliveryPointRequestBody,
  DeliveryPointDetailInterface,
  FetchDeliveryPointListQueryParams,
  FetchDeliveryPointListResponse,
  GDSForDeliveryPointNameInterface,
  UpdateDeliveryPointCharacteristicsProps,
  UpdateDeliveryPointRequestBody
} from '../eaiTypes';
import {
  setDeliveryPointAssignmentsList,
  setDeliveryPointCharacteristics,
  setDeliveryPointDetail,
  setDeliveryPointGDSDetail,
  setDeliveryPointGDSList,
  setEaiDeliveryPointList
} from '../eaiSlice';
import { EaiEtagEntityList } from 'models/etag';
import { setDispatcherDeliveryPoints } from '../../dispatcher/dispatcherSlice';
import { GET_LIST_WITHOUT_PAGINATION } from 'utils/constants';
import { logError } from 'services/Logging';

const deliveryPointPrefix = '/eai/delivery-point';

// ==============================|| THUNKS ||============================== //

export const fetchDeliveryPointList = async ({
  size = Number(GET_LIST_WITHOUT_PAGINATION),
  ...otherQueryParams
}: FetchDeliveryPointListQueryParams) => {
  const headers = createGetHeaderData(true, 'application/hal+json');
  const queryParams = createQueryParams({ size, ...otherQueryParams });
  const url = `${deliveryPointPrefix}${queryParams}`;

  try {
    const response: AxiosResponse<FetchDeliveryPointListResponse> =
      await apiClient.get(url, { headers });

    return response.data;
  } catch (e) {
    logError(e);
  }
};

type FetchAndSetDeliveryPointListParams = {
  isEai?: boolean;
} & FetchDeliveryPointListQueryParams;

export const fetchAndSetDeliveryPointList =
  ({ isEai, ...params }: FetchAndSetDeliveryPointListParams): AppThunk =>
  async (
    dispatch: Dispatch<
      ReturnType<
        typeof setEaiDeliveryPointList | typeof setDispatcherDeliveryPoints
      >
    >
  ) => {
    const responseData = await fetchDeliveryPointList(params);

    if (isEai) {
      dispatch(setEaiDeliveryPointList(responseData));
    } else {
      dispatch(setDispatcherDeliveryPoints(responseData));
    }
  };

export const fetchAndSetDeliveryPointDetail =
  (id?: string): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setDeliveryPointDetail>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    apiClient
      .get(`${deliveryPointPrefix}/detail/${id}`, {
        headers,
        data: { etagEntity: EaiEtagEntityList.DELIVERY_POINT }
      })
      .then((response: AxiosResponse) => {
        dispatch(setDeliveryPointDetail(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const createDeliveryPoint =
  (
    requestBody: CreateDeliveryPointRequestBody,
    callback?: (id: string) => void
  ): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*');

    apiClient
      .post(`${deliveryPointPrefix}`, { ...requestBody }, { headers })
      .then((response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.createDeliveryPoint'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback(response.data.id);
      })
      .catch((error: AxiosError) => logError(error));
  };

export const updateDeliveryPointDetail =
  (
    id: string,
    requestBody: UpdateDeliveryPointRequestBody,
    etag?: string,
    callback?: (data: DeliveryPointDetailInterface) => void
  ) =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof fetchAndSetDeliveryPointDetail>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(
      true,
      'application/hal+json',
      etag
    );

    apiClient
      .patch(
        `${deliveryPointPrefix}/detail/${id}`,
        { ...requestBody },
        { headers }
      )
      .then((response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.updateDeliveryPoint'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback(response.data);
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => {
            dispatch(fetchAndSetDeliveryPointDetail(id));
          },
          () =>
            dispatch(
              updateDeliveryPointDetail(id, requestBody, undefined, callback)
            )
        );
        logError(error);
      });
  };

export const getIsEicForDeliveryPointAvailable =
  (
    eic: string,
    deliveryPointId: string | undefined,
    callback?: (data: boolean) => void
  ): AppThunk =>
  () => {
    const headers: HttpHeaders = createGetHeaderData(true, '*/*');

    apiClient
      .get(
        `${deliveryPointPrefix}/eic-available${createQueryParams(
          deliveryPointId ? { eic: eic, id: deliveryPointId } : { eic: eic }
        )}`,
        { headers }
      )
      .then((response: AxiosResponse) => {
        callback && callback(response.data.available);
      })
      .catch((error: AxiosError) => logError(error));
  };

export const fetchAndSetDeliveryPointCharacteristics =
  (id?: string): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setDeliveryPointCharacteristics>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    apiClient
      .get(`${deliveryPointPrefix}/${id}/characteristics`, { headers })
      .then((response: AxiosResponse) => {
        dispatch(
          setDeliveryPointCharacteristics(response.data.characteristics)
        );
      })
      .catch((error: AxiosError) => logError(error));
  };

export const updateDeliveryPointCharacteristics =
  ({
    deliveryPointId,
    requestBody,
    etag,
    callback
  }: UpdateDeliveryPointCharacteristicsProps): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof fetchAndSetDeliveryPointDetail>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*', etag);

    apiClient
      .patch(
        `${deliveryPointPrefix}/${deliveryPointId}/characteristics`,
        { ...requestBody },
        { headers }
      )
      .then((response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t(
              'module.eai.messages.updateDeliveryPointCharacteristics'
            ),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback(response.data.characteristics);
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => dispatch(fetchAndSetDeliveryPointDetail(deliveryPointId)),
          () =>
            dispatch(
              updateDeliveryPointCharacteristics({
                deliveryPointId,
                requestBody,
                callback
              })
            )
        );
        logError(error);
      });
  };

export const getDeliveryPointGDSList =
  (
    id: string,
    searchString: string | undefined,
    validFromDateLt: string,
    validToDateGt: string,
    sort: string
  ): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setDeliveryPointGDSList>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    const queryParams = createQueryParams({
      search: searchString,
      validFromDateLt: validFromDateLt,
      validToDateGt: validToDateGt,
      sort: sort
    });

    apiClient
      .get(
        `${deliveryPointPrefix}/${id}/gateway-dispatcher-system${queryParams}`,
        {
          headers
        }
      )
      .then((response: AxiosResponse) => {
        dispatch(setDeliveryPointGDSList(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const unassignGDSFromDeliveryPoint =
  (id: string, gdsId: string, etag?: string, callback?: () => any) =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof fetchAndSetDeliveryPointDetail>
    >
  ) => {
    const headers: HttpHeaders = createPutHeaderData(
      true,
      'application/hal+json',
      etag
    );
    apiClient
      .patch(
        `${deliveryPointPrefix}/${id}/gateway-dispatcher-system/${gdsId}/invalidate`,
        {},
        { headers }
      )
      .then((_response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.unassignGDSFromDeliveryPoint'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => dispatch(fetchAndSetDeliveryPointDetail(id)),
          () =>
            dispatch(
              unassignGDSFromDeliveryPoint(id, gdsId, undefined, callback)
            )
        );
        logError(error);
      });
  };

export const registerGDSToDeliveryPoint =
  (
    id: string,
    requestBody: GDSForDeliveryPointNameInterface,
    etag?: string,
    callback?: () => any
  ) =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof fetchAndSetDeliveryPointDetail>
    >
  ) => {
    const headers: HttpHeaders = createPutHeaderData(
      true,
      'application/hal+json',
      etag
    );
    apiClient
      .put(
        `${deliveryPointPrefix}/${id}/gateway-dispatcher-system`,
        { ...requestBody },
        { headers }
      )
      .then((_response: AxiosResponse) => {
        //dispatch(getEmsMemberAccountDetail(EmsMemberAccountID));

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.registerGDSToDeliveryPoint'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => dispatch(fetchAndSetDeliveryPointDetail(id)),
          () =>
            dispatch(
              registerGDSToDeliveryPoint(id, requestBody, undefined, callback)
            )
        );
        logError(error);
      });
  };

export const getDeliveryPointGDSDetail =
  (id: string, gdsId: string): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setDeliveryPointGDSDetail>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    apiClient
      .get(`${deliveryPointPrefix}/${id}/gateway-dispatcher-system/${gdsId}`, {
        headers
      })
      .then((response: AxiosResponse) => {
        dispatch(setDeliveryPointGDSDetail(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const fetchAndSetDeliveryPointAssignments =
  (id: string, includeHistory?: boolean): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setDeliveryPointAssignmentsList>>) => {
    const headers = createGetHeaderData(true, 'application/hal+json');
    const queryParams = createQueryParams({ includeHistory });
    const url = `${deliveryPointPrefix}/${id}/assignments${queryParams}`;

    apiClient
      .get(url, { headers })
      .then((response: AxiosResponse) => {
        dispatch(
          setDeliveryPointAssignmentsList(
            response.data.deliveryPointAssignments
          )
        );
      })
      .catch((error: AxiosError) => logError(error));
  };

export const updateDeliveryPointGDSName =
  (
    id: string,
    gdsId: string,
    requestBody: GDSForDeliveryPointNameInterface,
    etag?: string,
    callback?: (data: GDSForDeliveryPointNameInterface) => void
  ): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof fetchAndSetDeliveryPointDetail>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*', etag);

    apiClient
      .patch(
        `${deliveryPointPrefix}/${id}/gateway-dispatcher-system/${gdsId}`,
        { ...requestBody },
        { headers }
      )
      .then((response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.updateDeliveryPointGDSName'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback(response.data);
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => dispatch(fetchAndSetDeliveryPointDetail(id)),
          () => {
            updateDeliveryPointGDSName(
              id,
              gdsId,
              requestBody,
              undefined,
              callback
            );
          }
        );
        logError(error);
      });
  };

export const createAndSetNewDeliveryPointAssignment =
  (
    id: string,
    requestBody: ChangeDeliveryPointAssignmentInterface,
    callback?: (id: string) => void
  ): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*');
    const url = `${deliveryPointPrefix}/${id}/assignment`;

    apiClient
      .post(url, requestBody, { headers })
      .then((response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.assignDeliveryPointChange'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback(response.data.id);
      })
      .catch((error: AxiosError) => {
        logError(error);
      });
  };
